import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Users } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { BrandMark } from "@/components/BrandMark";

/**
 * Standalone shell — used for AUTH and onboarding.
 *
 * Editorial split layout:
 *   ▸ Left rail (lg+): brand wordmark, eyebrow, headline, supporting copy,
 *     trust bullets, footer brand mark. Always-on; sets identity.
 *   ▸ Right rail: the form content. No card chrome — sits on white directly,
 *     so inputs feel like the focal element, not nested geometry.
 *
 * Stacks on mobile (left rail collapses to a slim header).
 *
 * Optional `accent` prop overrides the left-rail copy per surface
 * (e.g. onboarding shows progress copy instead of the marketing pitch).
 */
export function StandaloneShell({
  children,
  helper,
  footerNote,
  accent,
  contentMaxWidth = "560px",
  align = "center",   // legacy prop, no longer used — kept for back-compat
}) {
  const a = accent || DEFAULT_ACCENT;
  // We intentionally do NOT vertically centre any content. Both rails start
  // at the same top offset; brandmark sits at the bottom of the left rail
  // via mt-auto. This keeps the left and right content lines aligned at the
  // top of the page no matter how tall the form is.
  return (
    <div className="min-h-screen bg-surface grid lg:grid-cols-[480px_1fr] items-start">
      {/* ── Left rail (editorial, sticky on lg+) ───────────── */}
      <aside className="relative hidden lg:flex lg:sticky lg:top-0 lg:h-screen flex-col bg-surface-alt border-r border-line px-12 pt-10 pb-10 overflow-hidden">
        <div className="absolute inset-0 bg-grid-faint pointer-events-none opacity-70" />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(15,118,110,0.08), transparent)" }} />

        {/* Top: wordmark */}
        <Link to="/" className="relative z-10 inline-flex">
          <Wordmark size="md" />
        </Link>

        {/* Top-aligned content (matches the right rail's top offset of pt-14) */}
        <div className="relative z-10 mt-14">
          <div className="eyebrow mb-4">{a.eyebrow}</div>
          <h2 className="font-brand text-[36px] leading-[1.1] text-ink font-bold tracking-[-0.02em]">
            {a.headline}
          </h2>
          <p className="mt-5 text-body text-ink-muted leading-relaxed max-w-sm">
            {a.body}
          </p>

          <ul className="mt-8 space-y-3.5">
            {a.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-body-sm text-ink-muted">
                <span className="size-5 rounded-full bg-brand-50 text-brand-700 grid place-items-center mt-0.5 shrink-0">
                  <b.icon className="size-3" strokeWidth={2.4} />
                </span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom: brandmark (pinned via mt-auto) */}
        <div className="relative z-10 mt-auto flex items-center justify-between text-caption text-ink-hint pt-8">
          <BrandMark id={a.id || "001"} />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em]">
            {a.tag || "Members only"}
          </span>
        </div>
      </aside>

      {/* ── Right rail (form, top-aligned) ─────────────────── */}
      <main className="relative flex flex-col min-h-screen">
        {/* Mobile: compact header with wordmark */}
        <div className="lg:hidden border-b border-line px-6 py-5 flex items-center justify-between">
          <Link to="/"><Wordmark size="sm" /></Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-hint">
            {a.tag || "Members only"}
          </span>
        </div>

        {/* Top-aligned content. pt-14 lines up vertically with the left rail's
            mt-14 accent block (left rail also has pt-10 → its wordmark sits
            higher; the accent block + the right form start at the same Y). */}
        <div className="flex-1 px-6 lg:px-12 pt-12 lg:pt-[6.5rem] pb-12 lg:pb-14">
          <div className="w-full mx-auto" style={{ maxWidth: contentMaxWidth }}>
            {children}
            {helper && (
              <p className="mt-6 text-center text-caption text-ink-subtle">{helper}</p>
            )}
          </div>
        </div>

        {footerNote && (
          <div className="px-6 lg:px-12 pb-6 text-caption text-ink-hint">{footerNote}</div>
        )}
      </main>
    </div>
  );
}

const DEFAULT_ACCENT = {
  eyebrow: "Gift Card Buyers Club",
  headline: "Your closet cards, paid out by ACH.",
  body: "A members-only buying group that consolidates supply to negotiate better rates than any single seller could on their own.",
  bullets: [
    { icon: ShieldCheck, text: "KYC-verified members only" },
    { icon: Lock,        text: "Cards & bank accounts encrypted at rest with AWS KMS" },
    { icon: Users,       text: "Average payout in 38 hours from approval" },
  ],
  id: "001",
  tag: "Members only",
};

export default StandaloneShell;
